// P4 Wave 3 — Aba "Histórico" do CaseDetailPage (comitê).
//
// Timeline por-caso, metadado-only, semana-granular. Lê do endpoint ESTREITO
// GET /api/comite/cases/:id/audit (projectComite) — renderiza SÓ o que ele
// devolve (sem buscar dado de outro lugar; nada que o servidor já filtrou é
// re-exposto aqui). Visual idêntico às vizinhas: primitivo Table (.cf-table),
// tema dark, Alert/spinner globais. Independe de COMITE_READ_ENABLED (audit é
// metadado, não conteúdo) — mostra os eventos do rito que já existem.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Thead, Tbody, Tr, Th, Td, Alert, dataHora } from '@aions/ui-primitives';
import { apiFetch } from '../lib/auth-client';

interface Props {
  caseId: string;
  /** id→e-mail para nomear quem praticou o ato. Ausente → mostra o id cru, NUNCA
   *  omite a linha: omitir faria a trilha parecer ter menos atos do que tem.
   *  Mesma forma da prop do `ClassificacoesPanel` de propósito — os dois painéis
   *  leem o MESMO endpoint `/audit` e resolvem o mesmo `userId`; duas formas
   *  divergiriam no primeiro ajuste. */
  members?: ReadonlyArray<{ id: string; email: string }> | null;
}

/** Shape devolvido pelo endpoint estreito (projectComite). Campos opcionais
 *  porque o JSON omite undefined. NUNCA há campo proibido aqui (o servidor
 *  já projetou a allow-list). */
/**
 * Data e hora do evento, com a SEMANA como fallback — nunca vazio.
 *
 * Sem `createdAtEpochMs` a célula mostra a semana, e isso não é degradação
 * acidental: é o caso do ato do DENUNCIANTE, que por tipo não carrega hora. Cair
 * para "—" apagaria da tela um evento que existe na trilha, e a coluna deixaria de
 * bater com o `seq`. Mostrar a granularidade que o evento REALMENTE tem é o que
 * torna a assimetria legível em vez de parecer bug.
 */
/** Trilha de auditoria → COM segundos (dois atos no mesmo minuto precisam de
 *  ordem). O formatador vive em `lib/data-hora.ts`: eram 4 cópias, e duas já
 *  divergiam no `second` antes de qualquer revisor ver. */
function quando(e: TimelineEvent): string {
  return dataHora(e.createdAtEpochMs, e.createdWeek, { segundos: true });
}

interface TimelineEvent {
  seq: number;
  entryHash: string;
  createdWeek: string;
  /**
   * Data e hora do ato — o servidor JÁ projetava isto desde o ADR-0019 §7
   * (`audit-projection.ts:106`) e este cliente NUNCA leu: o campo não estava nem
   * declarado aqui, e a tabela mostrava só a semana. Produtor ligado, consumidor
   * não — o mesmo padrão da §"A CURA JÁ ESTAVA NO REPO" do CLAUDE.md.
   *
   * 🔴 STALE, CORRIGIDO EM 2026-08-13 — terceiro comentário da mesma família.
   *
   * Este bloco afirmava: "a hora só é carimbada quando o payload tem
   * `userId`/`actorId` — ato de pessoa IDENTIFICADA (...) é o que mantém o
   * LT-01/LT-26 de pé enquanto esta coluna existe: `status_consultado` continua só
   * com a semana".
   *
   * NADA disso vale mais. O gate de ator saiu de `carimbarHora`, `Phase1AuditPayload`
   * passou a declarar o campo, e `status_consultado` — o denunciante consultando a
   * própria denúncia — AGORA aparece com hora nesta coluna. Decisão do operador
   * (Opção C do ADR-0019 §6, que o próprio §6 havia rejeitado); ver ADR-0019 §8.
   *
   * Fica registrado em vez de apagado porque era ele que explicava por que a coluna
   * era segura, e um comentário que garante invariante morto lê-se como auditoria já
   * feita — o padrão do §"gêmeo que CITA O REQUISITO".
   */
  createdAtEpochMs?: number;
  eventType?: string;
  userId?: string;
  gravidade?: string;
  rota?: string;
  voto?: string;
  decisaoKind?: string;
  recursoVoto?: string;
  oitivaStatus?: string;
}

/** Detalhe inline (não-PII, rito): gravidade/voto/decisão/etc, quando houver. */
function detailOf(e: TimelineEvent): string | null {
  return e.gravidade ?? e.voto ?? e.decisaoKind ?? e.recursoVoto ?? e.oitivaStatus ?? null;
}

export function HistoricoPanel({ caseId, members }: Props): JSX.Element {
  const { t } = useTranslation();
  const emailPorId = new Map((members ?? []).map((m) => [m.id, m.email]));
  const [events, setEvents] = useState<TimelineEvent[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    setEvents(null);
    void (async () => {
      try {
        const res = await apiFetch(`/api/comite/cases/${caseId}/audit`);
        if (cancelled) return;
        if (!res.ok) {
          setError(true);
          return;
        }
        const body = (await res.json()) as { events: TimelineEvent[] };
        if (!cancelled) setEvents(body.events);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>{t('historico.titulo')}</h3>
      <p
        style={{
          fontSize: '0.85rem',
          color: 'var(--color-text-secondary)',
          marginBottom: 16,
          lineHeight: 1.55,
        }}
      >
        {t('historico.intro')}
      </p>

      {error ? (
        <Alert variant="warning" role="status">
          {t('historico.erro')}
        </Alert>
      ) : events === null ? (
        <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
          <span className="spinner" aria-hidden="true" /> {t('historico.carregando')}
        </p>
      ) : events.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
          {t('historico.vazio')}
        </p>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>{t('historico.col_evento')}</Th>
              <Th>{t('historico.col_quando', { defaultValue: 'Data e hora' })}</Th>
              <Th>{t('historico.col_membro')}</Th>
              <Th>{t('historico.col_integridade')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {events.map((e) => {
              const label = t(`historico.evento.${e.eventType ?? ''}`, {
                defaultValue: e.eventType ?? '—',
              });
              const detail = detailOf(e);
              return (
                <Tr key={e.seq}>
                  <Td>
                    {label}
                    {detail !== null && (
                      <span
                        style={{
                          color: 'var(--color-text-secondary)',
                          marginLeft: 6,
                          fontSize: '0.85rem',
                        }}
                      >
                        · {detail}
                      </span>
                    )}
                  </Td>
                  <Td mono>{quando(e)}</Td>
                  {/* E-MAIL, não o id truncado (pedido do operador 13/08). O id
                      cortado em 8 chars não identificava ninguém: para saber quem
                      agiu era preciso cruzar à mão com a lista de membros. Sem a
                      lista carregada, cai no id CRU — nunca em branco, porque
                      "ato sem autor" é afirmação diferente de "não sei o nome". */}
                  <Td mono>
                    {e.userId ? (emailPorId.get(e.userId) ?? e.userId) : t('historico.sem_membro')}
                  </Td>
                  {/* Hash COMPLETO: truncado em 12 chars ele não servia para o que
                      existe — conferir integridade contra o export judicial. A
                      classe quebra a linha para não estourar a tabela. */}
                  <Td mono>
                    <span className="hash-completo">{e.entryHash}</span>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </div>
  );
}
